import React from 'react';
import {
  AlertCircle, AlertTriangle, Briefcase, Building, CheckCircle,
  ChevronLeft, ChevronRight, Clock, Edit, Eye, EyeOff, Filter, Link2, Loader,
  Mail, MailOpen, Paperclip, Search, Star, Trash2, User, X, LifeBuoy, UserCog,
  Folder, FolderInput, FolderMinus, Check
} from 'lucide-react';
import MailEmailFilters from './MailEmailFilters';
import { useTranslation } from 'react-i18next';

/** Affiche le label d'un badge CRM : référence > nom court > label générique */
const badgeLabel = (badge, t) => {
  if (badge.reference) return badge.reference;
  const name = badge.name || '';
  // Si le name est une adresse email, ne pas l'afficher
  if (name.includes('@')) {
    return badge.type === 'courtier' ? t('mail.linkedCourtier', 'Courtier') 
         : badge.type === 'company'  ? t('mail.linkedCompany', 'Société')
         : t('mail.linkedClient', 'Client');
  }
  // Tronquer à 18 caractères si trop long
  return name.length > 18 ? name.substring(0, 18) + '…' : name;
};

const MailEmailListPanel = ({
  accountStats,
  bulkDelete, bulkMarkAsRead, bulkMarkAsSpam, bulkMarkAsUnread, bulkUnmarkSpam,
  cancelScheduledEmail, currentFolder, deleteEmail, draggedEmailId, emails,
  emailsPage, emailsPerPage, emptyFolder, filters, formatDate, formatDateParis, handleCRMLink,
  handleDragStart, handleEmailDragEnd, hasActiveFilters, loading, markAsSpam,
  openUnknownContactModal, resetFilters, scheduledEmails, scheduledEmailsLoading,
  searchQuery, selectEmail, selectedEmail, selectedEmailIds, setComposeForm,
  setEmailToLink, setEmailsPage, setEmailsPerPage, setSearchQuery, setShowComposeModal,
  setShowEmailLinkManager, setShowFilters, showFilters, toggleEmailSelection,
  toggleImportant, toggleSelectAll, unknownEmails, unmarkSpam, updateFilters,
  emailFolders, moveEmailToFolder
}) => {
  const { t } = useTranslation('admin');
  const [folderMenuFor, setFolderMenuFor] = React.useState(null);
  return (
            <div data-testid="mail-list-panel" className={`${selectedEmail ? 'hidden lg:flex' : 'flex'} lg:col-span-5 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex-col min-h-0`}>
            {/* Search Bar */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    data-testid="mail-search-input"
                    placeholder={t('mail.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setEmailsPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>
                <button
                  data-testid="mail-filters-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 rounded-xl transition-all flex items-center space-x-2 text-sm font-medium ${
                    showFilters || hasActiveFilters()
                      ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                      : 'bg-slate-100 text-slate-700 border-2 border-slate-200 hover:bg-slate-200'
                  }`}
                >
                  <Filter size={16} />
                  <span>{t('mail.filters')}</span>
                  {hasActiveFilters() && (
                    <span className="ml-1 px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                      ●
                    </span>
                  )}
                </button>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <MailEmailFilters
                  filters={filters}
                  updateFilters={updateFilters}
                  hasActiveFilters={hasActiveFilters}
                  resetFilters={resetFilters}
                />
              )}
            </div>

            {/* Bulk Actions Bar + Statistiques */}
            {emails.length > 0 && currentFolder !== 'scheduled' && (
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                {/* Gauche : checkbox + sélection + actions bulk */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="mail-select-all"
                    checked={selectedEmailIds.length === emails.length && emails.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600">
                    {selectedEmailIds.length > 0
                      ? t('mail.selectedCount', { count: selectedEmailIds.length })
                      : t('mail.selectAll')}
                  </span>
                </div>

                {/* Droite : actions bulk + stats */}
                <div className="flex items-center gap-2">

                  {/* Stats compact — visibles quand aucune sélection */}
                  {selectedEmailIds.length === 0 && accountStats && (
                    <div className="flex items-center gap-1.5">
                      <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-sm rounded font-medium">
                        {accountStats.total} {t('mail.statTotal')}
                      </span>
                      <span className="px-2.5 py-1 bg-green-100 text-green-600 text-sm rounded font-medium">
                        {accountStats.read} {t('mail.statRead')}
                      </span>
                      <span className="px-2.5 py-1 bg-blue-100 text-blue-600 text-sm rounded font-medium">
                        {accountStats.unread} {t('mail.statUnread')}
                      </span>
                      <span className="px-2.5 py-1 bg-orange-100 text-orange-600 text-sm rounded font-medium">
                        {accountStats.unlinked} {t('mail.statUnlinked')}
                      </span>
                    </div>
                  )}

                  {/* Bouton Vider la corbeille */}
                  {currentFolder === 'trash' && emptyFolder && (
                    <button
                      data-testid="mail-empty-trash"
                      onClick={() => {
                        if (window.confirm('Vider définitivement la corbeille ? Cette action est irréversible.')) {
                          emptyFolder('trash');
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                      title="Vider la corbeille définitivement"
                    >
                      <Trash2 size={14} />
                      <span>Vider la corbeille</span>
                    </button>
                  )}

                  {/* Bouton Vider le spam */}
                  {currentFolder === 'spam' && emptyFolder && (
                    <button
                      data-testid="mail-empty-spam"
                      onClick={() => {
                        if (window.confirm('Vider définitivement le dossier spam ? Cette action est irréversible.')) {
                          emptyFolder('spam');
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-1"
                      title="Vider le spam définitivement"
                    >
                      <AlertTriangle size={14} />
                      <span>Vider le spam</span>
                    </button>
                  )}

                  {selectedEmailIds.length > 0 && (
                    <>
                    <button
                      data-testid="mail-bulk-read"
                      onClick={bulkMarkAsRead}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1"
                      title={t('mail.markRead')}
                    >
                      <MailOpen size={14} />
                      <span>{t('mail.markReadShort')}</span>
                    </button>
                    
                    <button
                      data-testid="mail-bulk-unread"
                      onClick={bulkMarkAsUnread}
                      className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center space-x-1"
                      title={t('mail.markUnread')}
                    >
                      <Mail size={14} />
                      <span>{t('mail.markUnreadShort')}</span>
                    </button>
                    
                    {currentFolder !== 'spam' && (
                      <button
                        data-testid="mail-bulk-spam"
                        onClick={bulkMarkAsSpam}
                        className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-1"
                        title={t('mail.markSpam')}
                      >
                        <AlertTriangle size={14} />
                        <span>{t('mail.spamShort')}</span>
                      </button>
                    )}
                    
                    {currentFolder === 'spam' && (
                      <button
                        data-testid="mail-bulk-unspam"
                        onClick={bulkUnmarkSpam}
                        className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-1"
                        title={t('mail.unmarkSpam')}
                      >
                        <CheckCircle size={14} />
                        <span>{t('mail.unmarkSpamShort')}</span>
                      </button>
                    )}
                    
                    <button
                      data-testid="mail-bulk-delete"
                      onClick={bulkDelete}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center space-x-1 ${
                        currentFolder === 'trash'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      <Trash2 size={14} />
                      <span>{currentFolder === 'trash' ? t('mail.deleteShort') : t('mail.trashShort')}</span>
                    </button>
                  </>
                  )}
                </div>
              </div>
            )}

            {/* Email List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {currentFolder === 'scheduled' ? (
                // Liste des emails programmés
                scheduledEmailsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : scheduledEmails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                    <Clock size={48} className="mb-3" />
                    <p>{t('mail.noScheduled')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {scheduledEmails.map((scheduled) => {
                      const scheduledDate = new Date(scheduled.scheduled_datetime);
                      const isPending = scheduled.status === 'pending';
                      const isSent = scheduled.status === 'sent';
                      const isFailed = scheduled.status === 'failed';
                      
                      return (
                        <div
                          key={scheduled.id}
                          className="p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-semibold text-slate-900">
                                  {scheduled.subject || t('mail.noSubject')}
                                </span>
                                {isPending && (
                                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                    {t('mail.statusPending')}
                                  </span>
                                )}
                                {isSent && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                    {t('mail.statusSent')}
                                  </span>
                                )}
                                {isFailed && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                    {t('mail.statusFailed')}
                                  </span>
                                )}
                              </div>
                              
                              <div className="text-sm text-slate-600 mb-1">
                                <span className="font-medium">{t('mail.toLabel')}</span> {scheduled.to_addresses.join(', ')}
                              </div>
                              
                              {scheduled.cc_addresses && scheduled.cc_addresses.length > 0 && (
                                <div className="text-sm text-slate-500 mb-1">
                                  <span className="font-medium">{t('mail.ccLabel')}</span> {scheduled.cc_addresses.join(', ')}
                                </div>
                              )}
                              
                              <div className="flex items-center space-x-4 text-xs text-slate-500 mt-2">
                                <div className="flex items-center space-x-1">
                                  <Clock size={14} />
                                  <span>
                                    {t('mail.scheduledForDateTime', {
                                      date: scheduledDate.toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      }),
                                      time: scheduledDate.toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {isPending && (
                              <button
                                onClick={() => {
                                  if (window.confirm(t('mail.confirmCancelScheduled'))) {
                                    cancelScheduledEmail(scheduled.id);
                                  }
                                }}
                                className="ml-4 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center space-x-1 text-sm"
                              >
                                <X size={14} />
                                <span>{t('mail.cancelBtn')}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : emails.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Mail size={48} className="mb-3" />
                  <p>{t('mail.noEmailsInFolder')}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {emails.map((email) => (
                    <div
                      key={email.id}
                      data-testid={`mail-email-item-${email.id}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, email.id)}
                      onDragEnd={handleEmailDragEnd}
                      className={`group p-4 transition-colors hover:bg-slate-50 cursor-grab active:cursor-grabbing ${
                        selectedEmail?.id === email.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                      } ${!email.is_read ? 'bg-blue-50' : ''} ${
                        draggedEmailId === email.id ? 'opacity-50 bg-slate-100' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedEmailIds.includes(email.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleEmailSelection(email.id);
                          }}
                          className="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                        />
                        
                        <div 
                          onClick={() => selectEmail(email)}
                          className="flex-1 min-w-0 cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span className={`text-sm font-semibold truncate ${
                              !email.is_read ? 'text-slate-900' : 'text-slate-700'
                            } ${
                              email.direction === 'inbound' && unknownEmails.has(email.from_address?.toLowerCase()) ? 'text-red-600' : ''
                            }`}>
                              {email.direction === 'inbound' ? email.from_name || email.from_address : email.to_addresses[0]}
                            </span>
                            
                            {/* Badge Inconnu — masqué si l'email est déjà lié à au moins un contact CRM (y compris compagnie) */}
                            {email.direction === 'inbound' && unknownEmails.has(email.from_address?.toLowerCase()) && !(email.crm_badges?.length > 0) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openUnknownContactModal(email.from_address);
                                }}
                                className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium flex items-center space-x-1 hover:bg-red-200 transition-colors"
                                title={t('mail.unknownContact')}
                              >
                                <AlertCircle size={12} />
                                <span>{t('mail.unknown')}</span>
                              </button>
                            )}

                            {/* CRM Badges — un badge cliquable par liaison */}
                            {(email.crm_badges || []).map((badge, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const syntheticEmail =
                                    badge.type === 'client'             ? { linked_client_id: badge.id, linked_name: badge.name, linked_reference: badge.reference }
                                    : badge.type === 'courtier'         ? { linked_courtier_id: badge.id, linked_name: badge.name }
                                    : badge.type === 'assisteur'        ? { linked_assisteur_id: badge.id, linked_name: badge.name }
                                    : badge.type === 'assistance_client'? { linked_assistance_client_id: badge.id, linked_name: badge.name }
                                    :                                     { linked_company_id: badge.id, linked_name: badge.name };
                                  handleCRMLink(syntheticEmail);
                                }}
                                className={`flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                  badge.type === 'client'              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                  : badge.type === 'courtier'          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                  : badge.type === 'assisteur'         ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                  : badge.type === 'assistance_client' ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                                  :                                      'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                }`}
                                title={badge.name || badge.reference || ''}
                              >
                                {badge.type === 'client'              ? <User size={12} className="mr-1" />
                                : badge.type === 'courtier'           ? <Briefcase size={12} className="mr-1" />
                                : badge.type === 'assisteur'          ? <LifeBuoy size={12} className="mr-1" />
                                : badge.type === 'assistance_client'  ? <UserCog size={12} className="mr-1" />
                                :                                       <Building size={12} className="mr-1" />}
                                <span>{badgeLabel(badge, t)}</span>
                              </button>
                            ))}
                          </div>
                          
                          <p className={`text-sm truncate ${!email.is_read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                            {email.subject}
                          </p>
                          
                          {/* Badge email programmé */}
                          {email.is_scheduled && (
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium flex items-center space-x-1">
                                <Clock size={12} />
                                <span>{t('mail.scheduledForDateTime', { date: formatDateParis(email.scheduled_datetime, {day: '2-digit', month: '2-digit', year: 'numeric'}), time: formatDateParis(email.scheduled_datetime, {hour: '2-digit', minute: '2-digit'}) })}</span>
                              </span>
                            </div>
                          )}
                          
                          <p className="text-xs text-slate-500 truncate mt-1">
                            {email.body_text?.substring(0, 80)}...
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-end ml-3 space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-slate-500">
                              {formatDate(email.received_at || email.sent_at)}
                            </span>
                            
                            {!email.is_read && (
                              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
                                {t('mail.unreadBadge')}
                              </span>
                            )}
                            
                            {currentFolder === 'spam' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unmarkSpam(email.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-green-600 hover:text-green-800 hover:bg-green-50 p-1 rounded"
                                title={t('mail.unmarkSpam')}
                              >
                                <CheckCircle size={14} />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsSpam(email.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 hover:text-orange-800 hover:bg-orange-50 p-1 rounded"
                                title={t('mail.markSpam')}
                              >
                                <AlertCircle size={14} />
                              </button>
                            )}
                            
                            {/* Bouton lier à un contact */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEmailToLink(email);
                                setShowEmailLinkManager(true);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-1 rounded"
                              title={t('mail.linkToContact')}
                            >
                              <Link2 size={14} />
                            </button>
                            
                            {/* Bouton annuler pour emails programmés */}
                            {email.is_scheduled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(t('mail.confirmCancelScheduled'))) {
                                    cancelScheduledEmail(email.id);
                                  }
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 hover:text-orange-800 hover:bg-orange-50 p-1 rounded"
                                title={t('mail.cancelScheduled')}
                              >
                                <X size={14} />
                              </button>
                            )}
                            
                            {/* Bouton modifier pour emails programmés */}
                            {email.is_scheduled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Ouvrir le modal de composition avec les données de l'email
                                  setComposeForm({
                                    to: email.to_addresses.join(', '),
                                    cc: email.cc_addresses?.join(', ') || '',
                                    bcc: email.bcc_addresses?.join(', ') || '',
                                    subject: email.subject,
                                    body: email.body_html || '',
                                    enableTracking: email.tracking_enabled || false,
                                    scheduledSend: true,
                                    scheduledDate: new Date(email.scheduled_datetime).toISOString().split('T')[0],
                                    scheduledTime: new Date(email.scheduled_datetime).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})
                                  });
                                  setShowComposeModal(true);
                                  // Supprimer l'ancien email programmé
                                  cancelScheduledEmail(email.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded"
                                title={t('mail.editScheduled')}
                              >
                                <Edit size={14} />
                              </button>
                            )}
                            
                            {!email.is_scheduled && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteEmail(email.id, currentFolder === 'trash');
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded"
                              title={currentFolder === 'trash' ? t('mail.deletePermanentTitle') : t('mail.moveToTrashTitle')}
                            >
                              <Trash2 size={14} />
                            </button>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            {email.attachments?.length > 0 && (
                              <Paperclip size={14} className="text-slate-400" />
                            )}
                            
                            {/* Indicateur de lecture (pour emails envoyés) */}
                            {email.direction === 'outbound' && email.tracking_id && (
                              <div
                                className={`${
                                  email.opened_by_recipient 
                                    ? 'text-green-600' 
                                    : 'text-slate-400'
                                }`}
                                title={
                                  email.opened_by_recipient 
                                    ? t('mail.openedCount', { count: email.open_count || 1 })
                                    : t('mail.notOpenedByRecipient')
                                }
                              >
                                {email.opened_by_recipient ? (
                                  <Eye size={14} />
                                ) : (
                                  <EyeOff size={14} />
                                )}
                              </div>
                            )}
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleImportant(email.id, email.is_important);
                              }}
                              className={`transition-all hover:scale-110 ${
                                email.is_important 
                                  ? 'text-amber-500' 
                                  : 'text-slate-300 hover:text-amber-400'
                              }`}
                              title={email.is_important ? t('mail.removeImportant') : t('mail.markImportant')}
                            >
                              <Star 
                                size={16} 
                                className={email.is_important ? 'fill-amber-500' : ''} 
                              />
                            </button>

                            {/* Classer dans un dossier (action rapide) */}
                            <div className="relative">
                              <button
                                data-testid={`mail-list-folder-btn-${email.id}`}
                                onClick={(e) => { e.stopPropagation(); setFolderMenuFor(folderMenuFor === email.id ? null : email.id); }}
                                className={`transition-all hover:scale-110 ${email.folder_id ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                                title="Classer dans un dossier"
                              >
                                <FolderInput size={16} />
                              </button>
                              {folderMenuFor === email.id && (
                                <>
                                  <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setFolderMenuFor(null); }} />
                                  <div className="absolute right-0 top-6 z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[180px] max-h-56 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                    <p className="px-3 py-1 text-[11px] font-semibold text-slate-400 uppercase">Classer dans</p>
                                    {email.folder_id && (
                                      <button
                                        data-testid={`mail-list-folder-remove-${email.id}`}
                                        onClick={(e) => { e.stopPropagation(); setFolderMenuFor(null); moveEmailToFolder(email.id, null); }}
                                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <FolderMinus size={14} /> Retirer du dossier
                                      </button>
                                    )}
                                    {(emailFolders || []).length === 0 ? (
                                      <p className="px-3 py-2 text-xs text-slate-400">Aucun dossier</p>
                                    ) : (emailFolders || []).map(folder => (
                                      <button
                                        key={folder.id}
                                        data-testid={`mail-list-folder-${email.id}-${folder.id}`}
                                        disabled={email.folder_id === folder.id}
                                        onClick={(e) => { e.stopPropagation(); setFolderMenuFor(null); moveEmailToFolder(email.id, folder.id); }}
                                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${email.folder_id === folder.id ? 'text-slate-400 cursor-default' : 'text-slate-700 hover:bg-slate-100'}`}
                                      >
                                        <Folder size={14} className="text-amber-500" />
                                        <span className="truncate flex-1">{folder.name}</span>
                                        {email.folder_id === folder.id && <Check size={12} className="text-green-600" />}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Pagination des emails */}
              {!loading && emails.length > 0 && (
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between">
                    {/* Sélecteur de nombre d'emails par page */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-600">{t('mail.show')}</span>
                      <select
                        data-testid="mail-per-page-select"
                        value={emailsPerPage}
                        onChange={(e) => {
                          setEmailsPerPage(Number(e.target.value));
                          setEmailsPage(1);
                        }}
                        className="text-sm border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-slate-600">{t('mail.emails')}</span>
                    </div>
                    
                    {/* Contrôles de navigation */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-600">
                        {t('mail.page', { page: emailsPage })}
                      </span>
                      
                      <button
                        data-testid="mail-page-prev"
                        onClick={() => setEmailsPage(p => Math.max(1, p - 1))}
                        disabled={emailsPage === 1}
                        className="p-2 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('mail.prevPage')}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      
                      <button
                        data-testid="mail-page-next"
                        onClick={() => setEmailsPage(p => p + 1)}
                        disabled={emails.length < emailsPerPage}
                        className="p-2 rounded-lg border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('mail.nextPage')}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
  );
};

export default MailEmailListPanel;
